import {
  Injectable,
  Injector,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  ApplicationRef,
  TemplateRef
} from '@angular/core';
import Popper from 'popper.js';

import { PopoverComponent } from './popover.component';
import { PopoverRef } from './popover-ref';

export interface PopoverOptions {
  placement?: 'bottom' | 'top';
  maxWidth?: number;
  modalPopover?: boolean;
  injector?: Injector;
  customClasses?: string;
}

const DEFAULT_OPTIONS: PopoverOptions = {
  placement: 'bottom',
  maxWidth: 300,
  modalPopover: false,
  injector: null,
  customClasses: '',
}

/**
 * Popper Service
 *
 * This service is intended to create a popover that is tethered to
 * a specified host element. Content can also be passed into the
 * popper body in the form of a string, template or component.
 */
@Injectable()
export class PopoverService {
  constructor(
    private cfr: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) { }

  showPopover(element, content: any, options?: PopoverOptions): PopoverRef {
    // keep track of the number of component instances we create
    // so we can remove them from DOM and component tree properly
    // later on
    const components = [];

    // the element that conains the content for the popper
    let contentElem;

    // the popover ref we will return to the caller
    let popoverRef: PopoverRef;

    // set default options and override with passed options
    options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    // if a template is sent as content
    if (content instanceof TemplateRef) {
      contentElem = content.createEmbeddedView(null);
      this.appRef.attachView(contentElem);
      components.push(contentElem);
      contentElem = contentElem.rootNodes;

      // if a string is sent as content
    } else if (typeof content === 'string') {
      contentElem = document.createElement('div');
      contentElem.textContent = content;
      contentElem = [contentElem]; // component factory create is looking for an array type

      // if a component is sent as content
    } else if (typeof content === 'function') {
      const injector = options.injector ? options.injector : this.injector;
      const contentComponent = this.cfr.resolveComponentFactory(content).create(injector);
      this.appRef.attachView(contentComponent.hostView);
      components.push(contentComponent.hostView);
      contentElem = [contentComponent.location.nativeElement];
    }

    // Create a component factory for the popover component
    const componentFactory = this.cfr.resolveComponentFactory(PopoverComponent);

    // Create the component and inject the content to ng-content
    const componentRef = componentFactory.create(this.injector, [contentElem]);
    componentRef.instance.maxWidth = options.maxWidth + 'px';
    componentRef.instance.modalPopover = options.modalPopover;
    componentRef.instance.customClasses = options.customClasses;

    // Attach component to the appRef so that it's inside the ng component tree
    // this is needed so angular can keep track of the component and run
    // change detection and such
    this.appRef.attachView(componentRef.hostView);
    components.push(componentRef.hostView);

    // Get DOM element from component
    const popoverDomElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    // position DOM element as popper.
    let popper: Popper;

    let behavior: any;
    if (options.placement === 'top') {
      behavior = ['top', 'bottom']; // if popper is on top we need to redefine its behavior
    } else {
      behavior = ['bottom', 'top'];
    }

    setTimeout(() => {
      popper = new Popper(element, popoverDomElem, {
        placement: options.placement,
        modifiers: {
          flip: {
            behavior: behavior
          },
          offset: {
            offset: '0, 5px'
          },
          preventOverflow: {
            // the scroll parent needs to be viewport if modal since the modal body is a scrolling
            // element but we want the popper to go outside of it.
            boundariesElement: (() => options.modalPopover ? 'viewport' : 'scrollParent')()
          },
        }
      });

      popper.scheduleUpdate();

      // Append DOM element to the body
      document.body.appendChild(popoverDomElem);
    });

    // create a handy object that wraps everything
    // neccessary to manage the generated components also
    // create a proper destroy method that removes
    // the component from dom, ng component tree and
    // from popper.js
    const appRef = this.appRef; // quick closure
    popoverRef = {
      isShown: true,
      element: popoverDomElem,

      // update the popover
      update: function () {
        setTimeout(() => {
          popper.update();
        });
      },

      // create an all inclusive destroy function
      destroy: function () {
        // since the popover show is in a setTimeout I am
        // also wrapping this in setTimeout so it occurs after
        // the show function to avoid undefined popper error
        setTimeout(() => {
          for (const comp of components) {
            appRef.detachView(comp);
            comp.destroy();
          }
          components.slice(0, components.length);
          // document.body.removeChild(popoverDomElem); // appRef seems to remove the element from DOM
          popper.destroy();
          this.isShown = false;
        });
      }
    };

    return popoverRef;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/datepicker/popover/popover.service.ts